import {
  getPeriodEndEventLabel,
  isInjuryTimeEvent,
  isPeriodStartEvent,
  mapInjuryTimeToAddedTimeRow,
  mapOneEventToCard,
  mapPeriodStartToCard,
  mapSegmentWithInjuryTimeRows,
  shouldShowInMinutoTimeline,
  splitLiveEventsByHalf,
} from "./mapLiveEventsToResumen";
import { getMatchTeamRefs } from '../../utils/matchTeamRefUtils';

/**
 * Línea LARGA: el suceso anterior y el actual ocupan lados distintos de la línea (local `left`
 * vs visita `right`) → hay contenido a ambos lados del eje. Incluye mismo minuto o cualquier
 * par de minutos distintos siempre que cambie el bando.
 * Línea CORTA: mismo bando en ambas filas consecutivas (solo un lado del eje).
 */
function shouldUseLongLineBetween(prevCard, currCard) {
  const sa = prevCard?.side;
  const sb = currCard?.side;
  if (sa !== "left" && sa !== "right") return false;
  if (sb !== "left" && sb !== "right") return false;
  if (sa === sb) return false;
  return true;
}

function withMatchMinuteFromApi(api, refs) {
  return mapOneEventToCard(api, refs, true);
}

function mapApiToMinutoEvent(api, refs) {
  if (isPeriodStartEvent(api)) {
    return mapPeriodStartToCard(api, refs);
  }
  if (isInjuryTimeEvent(api)) {
    return mapInjuryTimeToAddedTimeRow(api);
  }
  return withMatchMinuteFromApi(api, refs);
}

/**
 * Añade `minutoLineSegment`: 'none' | 'short' | 'long' en cada suceso con minuto
 * (coherente con tramos largo/corto de la maqueta).
 */
function annotateMinutoLineSegments(rows) {
  let lastCard = null;
  let isFirstPlayMinuteSlot = true;
  for (let i = 0; i < rows.length; i += 1) {
    const ev = rows[i];
    if (ev.type === "halftime") {
      lastCard = null;
      continue;
    }
    if (ev.type === "added_time") {
      lastCard = null;
      continue;
    }
    if (ev.minute == null) {
      continue;
    }
    if (isFirstPlayMinuteSlot) {
      ev.minutoLineSegment = "none";
      isFirstPlayMinuteSlot = false;
    } else if (!lastCard) {
      ev.minutoLineSegment = "short";
    } else {
      ev.minutoLineSegment = shouldUseLongLineBetween(lastCard, ev) ? "long" : "short";
    }
    lastCard = ev;
  }
}

/**
 * Misma presentación que el mock `events`: 2.ª parte (minuto desc) → fin 1.ª / medio tiempo
 * → 1.ª parte (minuto desc).
 * @param {object|undefined} matchDetailsData — cuerpo `data` del servicio.
 * @returns {{ fromLive: true, events: object[] } | null}
 */
export function mapLiveEventsToMinutoTimeline(matchDetailsData) {
  const block = matchDetailsData?.liveEvents?.[0];
  const raw = block?.events;
  if (!Array.isArray(raw) || raw.length === 0) {
    return null;
  }

  const card = matchDetailsData?.matchCard?.[0];
  const md = card?.summary?.matchDetails ?? matchDetailsData?.matchDetails;
  const refs = getMatchTeamRefs(md);

  const { halfEnd, firstSlice, secondSlice } = splitLiveEventsByHalf(raw);
  const playFilter = (e) => shouldShowInMinutoTimeline(e, refs);
  const firstPlay = firstSlice.filter(playFilter);
  const secondPlay = secondSlice.filter(playFilter);

  if (firstPlay.length === 0 && secondPlay.length === 0) {
    return null;
  }

  const mapMinutoPlay = (api) => mapApiToMinutoEvent(api, refs);

  let rows;
  if (halfEnd < 0) {
    rows = [...mapSegmentWithInjuryTimeRows(firstPlay, mapMinutoPlay)];
  } else {
    const halfTimeLabel = getPeriodEndEventLabel(raw[halfEnd]);
    rows = [
      ...mapSegmentWithInjuryTimeRows(secondPlay, mapMinutoPlay),
      {
        minute: null,
        side: "center",
        type: "halftime",
        label: halfTimeLabel,
      },
      ...mapSegmentWithInjuryTimeRows(firstPlay, mapMinutoPlay),
    ];
  }

  annotateMinutoLineSegments(rows);
  return { fromLive: true, events: rows };
}
